import { StickyNote, Clock, Check, XCircle, AlertTriangle } from 'lucide-react'
import type {
  ShiftAssignment,
  Shift,
  ShiftAssignmentShiftRef,
} from '@/types/shift.types'
import { getAssignmentTooltipText } from '../utils/assignmentUtils'
import {
  getAssignmentStatusColor,
} from '../utils/colorUtils'
import { getCompanyColor } from '../utils/companyColor'
import { eligibilityWarningText } from '../utils/shiftEligibility'
import { cn } from '@/lib/cn'

export interface ShiftBlockProps {
  assignment: ShiftAssignment
  shift?: Shift | ShiftAssignmentShiftRef
  onClick: () => void
  /** Kept for backwards compat — controls vertical padding only; layout is now uniform. */
  variant?: 'default' | 'monthly'
  className?: string
  /** When provided, renders a small color dot identifying the company. */
  companyId?: string
  /** Optional company name (used in tooltip). */
  companyName?: string
  labelOverride?: string
  timeRangeOverride?: string
}

function StatusIcon({
  status,
  contrast,
}: {
  status: ShiftAssignment['status']
  contrast: 'light' | 'dark'
}): React.ReactElement {
  const tone =
    contrast === 'light'
      ? 'text-white/90'
      : 'text-gray-700 dark:text-gray-100'
  switch (status) {
    case 'confirmed':
      return <Check className={cn('h-3 w-3 shrink-0', tone)} aria-hidden />
    case 'cancelled':
      return <XCircle className={cn('h-3 w-3 shrink-0', tone)} aria-hidden />
    default:
      return <Clock className={cn('h-3 w-3 shrink-0', tone)} aria-hidden />
  }
}

/**
 * Unified shift block — identical card layout across daily / weekly / monthly views:
 *   [companyDot] label                              hh:mm – hh:mm  📝  ✓
 *
 * Colour logic:
 *   1. Shift template's own `color` (admin-defined) is the primary fill.
 *   2. Falls back to assignment-status colour when no template colour is set.
 *
 * Status: conveyed by the small icon on the right + opacity dim + line-through for cancelled.
 */
export function ShiftBlock({
  assignment,
  shift,
  onClick,
  variant = 'default',
  className,
  companyId,
  companyName,
  labelOverride,
  timeRangeOverride,
}: ShiftBlockProps): React.ReactElement {
  const label = labelOverride ?? shift?.name ?? 'Shift'
  const timeRange =
    timeRangeOverride ??
    (shift?.startTime && shift?.endTime
      ? `${shift.startTime} – ${shift.endTime}`
      : '')
  const fill =
    assignment.color ||
    (shift && 'color' in shift && shift.color) ||
    getAssignmentStatusColor(assignment.status)
  const baseTitle = getAssignmentTooltipText(assignment)
  // Set by the admin list when the person became ineligible after scheduling.
  const eligibilityWarning =
    assignment.status !== 'cancelled' && assignment.eligibilityIssues?.length
      ? eligibilityWarningText(assignment.eligibilityIssues)
      : null
  const titleWithCompany = companyName ? `${companyName} · ${baseTitle}` : baseTitle
  const title = eligibilityWarning ? `${eligibilityWarning}\n${titleWithCompany}` : titleWithCompany
  const hasNotes = Boolean(assignment.notes?.trim())
  const isCancelled = assignment.status === 'cancelled'
  const companyColor = companyId ? getCompanyColor(companyId) : null
  const isMonthly = variant === 'monthly'

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative block w-full cursor-pointer rounded-md border-l-[3px] text-left text-xs font-medium shadow-sm ring-1 ring-black/5 transition-all hover:-translate-y-px hover:shadow-md hover:ring-2 hover:ring-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
        isMonthly ? 'min-h-[32px] px-2 py-1' : 'min-h-[46px] px-2.5 py-1.5',
        'text-gray-800',
        isCancelled && 'opacity-60 line-through decoration-1',
        eligibilityWarning && 'ring-2 ring-amber-400',
        className
      )}
      style={{
        backgroundColor: `color-mix(in srgb, ${fill} 13%, white)`,
        borderLeftColor: fill,
      }}
      title={title}
    >
      {timeRange && (
        <span
          className="block truncate pr-6 text-[11px] font-semibold"
          style={{ color: fill }}
        >
          {timeRange}
        </span>
      )}
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-[10px] font-semibold">
        {companyColor && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: companyColor.dot }} aria-hidden />
        )}
        <span className="truncate">{label}</span>
      </span>
      <span className="absolute right-2 top-2 flex items-center gap-1 text-gray-500">
        {eligibilityWarning ? (
          <span data-testid="shift-eligibility-warning" className="text-amber-600">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            <span className="sr-only">{eligibilityWarning}</span>
          </span>
        ) : null}
        {hasNotes ? <StickyNote className="h-3 w-3" aria-hidden /> : null}
        <StatusIcon status={assignment.status} contrast="dark" />
      </span>
    </button>
  )
}

ShiftBlock.displayName = 'ShiftBlock'
